import { Component, Input, Output, EventEmitter, OnInit } from '@angular/core';
import { CommonModule } from '@angular/common';
import { FormsModule } from '@angular/forms';
import { DomSanitizer, SafeHtml, SafeResourceUrl } from '@angular/platform-browser';
import { ModalController, IonicModule } from '@ionic/angular';
import { QuillModule } from 'ngx-quill';
import {
  IParaclinico,
  IHistorico,
} from '../paraclinicos/paraclinicos-page/interfaces/paraclinico.interface';
import { HistoricoModalComponent } from './historico-modal/historico-modal.component';

@Component({
  selector: 'app-historico',
  standalone: true,
  imports: [CommonModule, FormsModule, QuillModule, IonicModule],
  templateUrl: './historico.component.html',
  styleUrls: ['./historico.component.scss'],
})
export class HistoricoComponent implements OnInit {
  @Input() isLocked: boolean = false;
  @Input() historico: IHistorico[] = [];
  @Input() appointmentId: number | null = null;
  @Input() patientId: number | null = null;
  @Input() paraclinicosDisponibles: IParaclinico[] = [];
  @Input() tituloSeccion: string = 'Histórico';

  @Output() onSave = new EventEmitter<IHistorico[]>();
  @Output() onVerParaclinico = new EventEmitter<IParaclinico>();

  // Modal Ver Paraclínico
  modalVerParaclinicoAbierto = false;
  paraclinicoParaVer: (IParaclinico & { interpretacionSafe?: SafeHtml; pdfUrlSafe?: SafeResourceUrl }) | null = null;

  // Modal de Confirmación
  modalConfirmacionAbierto = false;
  tipoConfirmacion: 'eliminar-historico' | 'eliminar-evento' | null = null;
  datosConfirmacion: {
    titulo?: string;
    histIndex?: number;
    eventoIndex?: number;
    eventoNombre?: string;
    cantidadEventos?: number;
    esUltimoEvento?: boolean;
  } | null = null;

  // Estado de colapso por título (índice del histórico)
  collapsedHistoricos: Set<number> = new Set();

  constructor(
    private sanitizer: DomSanitizer,
    private modalController: ModalController
  ) {}

  ngOnInit(): void {}

  toggleHistoricoCollapse(index: number): void {
    if (this.collapsedHistoricos.has(index)) {
      this.collapsedHistoricos.delete(index);
    } else {
      this.collapsedHistoricos.add(index);
    }
  }

  isHistoricoCollapsed(index: number): boolean {
    return this.collapsedHistoricos.has(index);
  }

  get historicosValidos(): IHistorico[] {
    return this.historico.filter((h) => h.titulo && h.titulo.trim() !== '' && h.eventos && h.eventos.length > 0);
  }

  async abrirModalHistorico(): Promise<void> {
    const modal = await this.modalController.create({
      component: HistoricoModalComponent,
      cssClass: 'historico-modal',
      componentProps: {
        historico: this.historico,
        paraclinicosDisponibles: this.paraclinicosDisponibles,
        modoEdicion: false,
        modoEdicionHistorico: false,
      },
    });

    await modal.present();

    const { data } = await modal.onWillDismiss();
    if (data) {
      this.procesarResultadoModal(data);
    }
  }

  async abrirModalEditarHistorico(historicoItem: IHistorico): Promise<void> {
    const indiceHistoricoEnEdicion = this.historico.findIndex((h) => h === historicoItem);

    if (indiceHistoricoEnEdicion === -1) {
      console.error('No se encontró el histórico en el array original');
      return;
    }

    const modal = await this.modalController.create({
      component: HistoricoModalComponent,
      cssClass: 'historico-modal',
      componentProps: {
        historico: this.historico,
        paraclinicosDisponibles: this.paraclinicosDisponibles,
        modoEdicion: false,
        modoEdicionHistorico: true,
        historicoParaEditar: historicoItem,
        indiceHistoricoEnEdicion,
      },
    });

    await modal.present();

    const { data } = await modal.onWillDismiss();
    if (data) {
      this.procesarResultadoModal(data);
    }
  }

  private procesarResultadoModal(data: any): void {
    const { nuevoHistorico, modoEdicion, modoEdicionHistorico, indiceHistoricoSeleccionado, indiceHistoricoEnEdicion } =
      data;

    if (modoEdicionHistorico && indiceHistoricoEnEdicion !== null) {
      this.historico[indiceHistoricoEnEdicion] = { ...nuevoHistorico };
    } else if (modoEdicion && indiceHistoricoSeleccionado !== null) {
      this.historico[indiceHistoricoSeleccionado].eventos.push(...nuevoHistorico.eventos);
    } else {
      this.historico.push({ ...nuevoHistorico });
    }

    this.onSave.emit(this.historico);
  }

  eliminarHistorico(historicoItem: IHistorico): void {
    const index = this.historico.findIndex((h) => h === historicoItem);

    if (index === -1) {
      console.error('No se encontró el histórico en el array original');
      return;
    }

    const historico = this.historico[index];

    this.tipoConfirmacion = 'eliminar-historico';
    this.datosConfirmacion = {
      titulo: historico.titulo,
      histIndex: index,
      cantidadEventos: historico.eventos.length,
    };
    this.modalConfirmacionAbierto = true;
  }

  eliminarHistoricoConfirmado(index: number): void {
    this.historico.splice(index, 1);
    this.onSave.emit(this.historico);
    this.cerrarModalConfirmacion();
  }

  eliminarEvento(historicoItem: IHistorico, eventoIndex: number): void {
    const histIndex = this.historico.findIndex((h) => h === historicoItem);

    if (histIndex === -1) {
      console.error('No se encontró el histórico en el array original');
      return;
    }

    const historico = this.historico[histIndex];
    const evento = historico.eventos[eventoIndex];
    const esUltimoEvento = historico.eventos.length === 1;

    this.tipoConfirmacion = 'eliminar-evento';
    this.datosConfirmacion = {
      histIndex,
      eventoIndex,
      eventoNombre: evento.nombre,
      esUltimoEvento,
    };
    this.modalConfirmacionAbierto = true;
  }

  eliminarEventoConfirmado(histIndex: number, eventoIndex: number): void {
    this.historico[histIndex].eventos.splice(eventoIndex, 1);

    if (this.historico[histIndex].eventos.length === 0) {
      this.historico.splice(histIndex, 1);
    }

    this.onSave.emit(this.historico);
    this.cerrarModalConfirmacion();
  }

  cerrarModalConfirmacion(): void {
    this.modalConfirmacionAbierto = false;
    this.tipoConfirmacion = null;
    this.datosConfirmacion = null;
  }

  confirmarAccion(): void {
    if (this.tipoConfirmacion === 'eliminar-historico' && this.datosConfirmacion?.histIndex !== undefined) {
      this.eliminarHistoricoConfirmado(this.datosConfirmacion.histIndex);
    } else if (
      this.tipoConfirmacion === 'eliminar-evento' &&
      this.datosConfirmacion?.histIndex !== undefined &&
      this.datosConfirmacion?.eventoIndex !== undefined
    ) {
      this.eliminarEventoConfirmado(this.datosConfirmacion.histIndex, this.datosConfirmacion.eventoIndex);
    }
  }

  getTituloConfirmacion(): string {
    if (this.tipoConfirmacion === 'eliminar-historico') {
      return '¿Eliminar histórico?';
    } else if (this.tipoConfirmacion === 'eliminar-evento') {
      return '¿Eliminar evento?';
    }
    return '';
  }

  getMensajeConfirmacion(): string {
    if (this.tipoConfirmacion === 'eliminar-historico') {
      const titulo = this.datosConfirmacion?.titulo || 'este histórico';
      const cantidad = this.datosConfirmacion?.cantidadEventos || 0;
      return `Se eliminará "${titulo}" y todos sus ${cantidad} evento${cantidad !== 1 ? 's' : ''} asociado${
        cantidad !== 1 ? 's' : ''
      }.`;
    } else if (this.tipoConfirmacion === 'eliminar-evento') {
      const nombre = this.datosConfirmacion?.eventoNombre || 'este evento';
      return `Se eliminará el evento "${nombre}".`;
    }
    return '';
  }

  mostrarAdvertenciaExtra(): boolean {
    return this.tipoConfirmacion === 'eliminar-evento' && !!this.datosConfirmacion?.esUltimoEvento;
  }

  async abrirModalVerParaclinico(paraclinico: any): Promise<void> {
    // Emitir evento al componente padre para que abra el modal a pantalla completa
    this.onVerParaclinico.emit(paraclinico);
  }

  cerrarModalVerParaclinico(): void {
    this.modalVerParaclinicoAbierto = false;
    this.paraclinicoParaVer = null;
  }

  compartirParaclinicoPorWhatsapp(pdfUrl: string | undefined): void {
    if (!pdfUrl) return;
    const url = `https://wa.me/?text=${encodeURIComponent('Te comparto el estudio: ' + pdfUrl)}`;
    window.open(url, '_blank');
  }

  guardarInterpretacionParaclinico(): void {
    if (!this.paraclinicoParaVer) return;

    const index = this.paraclinicosDisponibles.findIndex((p) => p.id === this.paraclinicoParaVer?.id);

    if (index !== -1) {
      this.paraclinicosDisponibles[index].interpretacion = this.paraclinicoParaVer.interpretacion || '';
      console.log('[Historico] Interpretación actualizada:', this.paraclinicoParaVer.interpretacion);
      console.warn('[Historico] IMPORTANTE: Implementar guardado de interpretación en backend');
    }

    this.cerrarModalVerParaclinico();
  }

  getData(): IHistorico[] {
    return this.historico;
  }
}
